/**
 * @file ExtraActions.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Tuesday, 8th June 2021 10:59:14 am
 * @copyright 2015 - 2021 SKAT LLC, Delive LLC
 * @flow
 */
import type { iGlobalEventBus, iCRUD, UserPreferences } from '../../types'

import * as React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import autoBind from 'react-autobind'
import { __ } from '../../globals'
import { Inject } from '../../serviceLocator'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faBell, faBellSlash, faEdit, faBoxTissue } from '@fortawesome/free-solid-svg-icons'
import debounce from 'lodash/debounce'
import isNil from 'lodash/isNil'

library.add(faBell, faBellSlash, faEdit, faBoxTissue)

const DRAFT_TEXT_PREF_NAME = 'draft-text'
const NOTIFICATIONS_MODE_PREF_NAME = 'notifications-concealed'

type Props = {
  contextView: React.Node,
  onCollapseAll: () => void
}

type State = {
  editorVisible: boolean,
  notificationsVisible: boolean
}

class DraftEditor extends React.Component<{}, {text: string}> {
  @Inject preferencesManager : iCRUD<UserPreferences>
  updatePrefs: (text: string) => Promise<void>

  constructor () {
    super()
    autoBind(this)
    this.state = {
      text: ''
    }

    this.updatePrefs = debounce(async (text) => {
      await this.preferencesManager.update({
        name: DRAFT_TEXT_PREF_NAME,
        value: text
      })
    }, 200)
  }

  async componentDidMount () {
    const pref = await this.preferencesManager.read(DRAFT_TEXT_PREF_NAME)
    if (pref && pref.value) this.setState({ text: pref.value })
  }

  handleTextChange (event: any) {
    const text = event.target.value
    this.setState({ text })
    this.updatePrefs(text)
  }

  render () : React.Node {
    const { text } = this.state
    return (
      <div className='draft-edit'>
        <textarea value={text} placeholder={__('THIS_IS_DRAFT')} onChange={this.handleTextChange} />
      </div>
    )
  }
}

class ExtraActions extends React.Component<Props, State> {
  @Inject globalEventBus : iGlobalEventBus
  @Inject preferencesManager : iCRUD<UserPreferences>

  constructor () {
    super()
    autoBind(this)
    this.state = {
      editorVisible: false,
      notificationsVisible: true
    }
  }

  async componentDidMount () {
    this.globalEventBus.registerEvent('notifications-mode-changed')
    const pref = await this.preferencesManager.read(NOTIFICATIONS_MODE_PREF_NAME)
    if (pref && !isNil(pref.value)) this.setState({ notificationsVisible: pref.value })
  }

  handleEditorToggle () {
    const { editorVisible } = this.state
    this.setState({ editorVisible: !editorVisible })
  }

  handleNotificationsModeToggle () {
    const { notificationsVisible } = this.state
    this.setState({ notificationsVisible: !notificationsVisible }, async () => {
      const visible = this.state.notificationsVisible
      this.preferencesManager.update({
        name: NOTIFICATIONS_MODE_PREF_NAME,
        value: visible
      })
      this.globalEventBus.emit('notifications-mode-changed', { visible })
    })
  }

  render () : React.Node {
    const { contextView, onCollapseAll } = this.props
    const { notificationsVisible, editorVisible } = this.state
    return (
      <>
        {editorVisible ? <DraftEditor /> : null}
        <div className='context-block'>{contextView}</div>
        <div className='extra-context-button draft' title={__('OPEN_DRAFT_EDITOR')} onClick={this.handleEditorToggle}>
          <FontAwesomeIcon className='icon marker' icon={editorVisible ? 'box-tissue' : 'edit'} />
        </div>
        <div className='extra-context-button notifications' title={__('VISUAL_MUTE_NOTIFICATIONS')} onClick={this.handleNotificationsModeToggle}>
          <FontAwesomeIcon className='icon marker' icon={notificationsVisible ? 'bell' : 'bell-slash'} />
        </div>
        <div title={__('COLLAPSE_ALL_WINDOWS')} className='modals-collapse' onClick={onCollapseAll} />
      </>
    )
  }
}

export default ExtraActions
